import { effectiveTaxCode, marginBase, taxCodeDefinition } from '../taxCodes';
import { calculateInvoiceLineNet, type Invoice } from '../types';
import type { SellerSettings } from './sellerSettings';
import { taxSubtotals, type EInvoiceBuyer } from './xrechnung';
import { unitCodeOrDefault } from './units';

/**
 * Die Geschäftsregeln der EN 16931, geprüft BEVOR eine XRechnung entsteht.
 *
 * WARUM es das gibt: Der Erzeuger konnte bis 09/2026 jede Rechnung in XML gießen -- ob das Ergebnis
 * gültig war, erfuhr man frühestens beim Empfänger, der es abweist. Ein zurückgewiesener Beleg ist
 * teurer als ein nicht erzeugter: die Rechnung gilt als nicht gestellt, die Frist läuft weiter.
 *
 * WAS DAS HIER NICHT IST: der offizielle KoSIT-Prüfer. Der ist das einzige Werkzeug, das eine
 * XRechnung zertifizieren kann, läuft in Java und prüft zusätzlich Schema und Schematron. Diese
 * Datei bildet die Regeln ab, die sich aus den FORQ-Daten heraus prüfen lassen -- sie fängt ab,
 * was man ohne ihn sehen kann, und ersetzt ihn nicht. Siehe `docs/howto/e-rechnung.md`.
 *
 * Die Regelnummern stehen in den Meldungen, damit ein Fund im offiziellen Prüfbericht
 * wiederzufinden ist.
 */

export interface En16931Finding {
  /** Regelkennung der EN 16931, z. B. „BR-25" -- so steht sie auch im KoSIT-Prüfbericht. */
  readonly rule: string;
  /** Klartext, an dem der Anwender erkennt, WELCHEN Datensatz er reparieren muss. */
  readonly message: string;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Beträge dürfen um einen Cent auseinanderliegen -- jede Seite rundet einmal. */
function nearlyEqual(a: number, b: number, tolerance = 0.01): boolean {
  return Math.abs(a - b) <= tolerance;
}

function euro(value: number): string {
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

/**
 * Prüft Rechnung, Verkäufer und Empfänger gegen die EN 16931. Leeres Ergebnis = aus FORQs Sicht
 * in Ordnung; jeder Eintrag ist ein Grund, die XML NICHT zu erzeugen.
 */
export function validateEn16931(
  invoice: Invoice,
  seller: SellerSettings,
  buyer: EInvoiceBuyer,
): readonly En16931Finding[] {
  const findings: En16931Finding[] = [];
  const add = (rule: string, message: string) => findings.push({ rule, message });

  // --- Kopfangaben -----------------------------------------------------------------------------
  if (!invoice.invoiceNumber?.trim()) {
    add('BR-02', 'Der Beleg hat keine Rechnungsnummer -- ein Entwurf lässt sich nicht als E-Rechnung ausgeben.');
  }
  if (!invoice.issueDate?.trim()) add('BR-03', 'Das Rechnungsdatum fehlt.');

  // --- Verkäufer -------------------------------------------------------------------------------
  if (!seller.name.trim()) add('BR-06', 'Der Firmenname fehlt in der Grundkonfiguration.');
  if (!seller.street.trim() || !seller.postalCode.trim() || !seller.city.trim()) {
    add('BR-08', 'Die eigene Anschrift ist unvollständig (Straße, PLZ, Ort in der Grundkonfiguration).');
  }
  if (!/^[A-Z]{2}$/.test(seller.countryCode)) add('BR-09', 'Der eigene Ländercode fehlt (z. B. DE).');
  // BT-34: ohne elektronische Adresse weist der KoSIT-Prüfer den Beleg ab (PEPPOL-EN16931-R020).
  if (!seller.contactEmail.trim()) {
    add('PEPPOL-EN16931-R020', 'Die eigene E-Mail-Adresse fehlt in der Grundkonfiguration -- die E-Rechnung verlangt eine elektronische Adresse des Verkäufers.');
  }
  if (!seller.vatId.trim() && !seller.taxNumber.trim()) {
    add('BR-CO-26', 'Ohne USt-IdNr. oder Steuernummer lässt sich der Verkäufer nicht eindeutig bestimmen.');
  }

  // --- Empfänger -------------------------------------------------------------------------------
  if (!buyer.name.trim()) add('BR-07', 'Der Kunde hat keinen Namen.');
  if (!buyer.street.trim() || !buyer.postalCode.trim() || !buyer.city.trim()) {
    add('BR-10', `Die Rechnungsanschrift von ${buyer.name || 'dem Kunden'} ist unvollständig.`);
  }
  if (!/^[A-Z]{2}$/.test(buyer.countryCode)) add('BR-11', 'Der Ländercode des Kunden fehlt.');
  // BT-49: dasselbe für den Empfänger (PEPPOL-EN16931-R010).
  if (!buyer.email.trim()) {
    add('PEPPOL-EN16931-R010', `Für ${buyer.name || 'den Kunden'} ist keine E-Mail-Adresse hinterlegt -- die E-Rechnung verlangt eine elektronische Adresse des Empfängers.`);
  }

  // --- Positionen ------------------------------------------------------------------------------
  if (invoice.lines.length === 0) {
    add('BR-16', 'Die Rechnung hat keine Position -- eine E-Rechnung braucht mindestens eine.');
  }
  invoice.lines.forEach((line, index) => {
    const nummer = index + 1;
    if (!line.description.trim()) {
      add('BR-25', `Position ${nummer} hat keine Bezeichnung. In der E-Rechnung ist sie Pflicht -- auch bei einer Textzeile.`);
    }
    if (!unitCodeOrDefault(line.unit)) {
      add('BR-23', `Position ${nummer}: die Einheit „${line.unit}" lässt sich keiner Maßeinheit zuordnen.`);
    }
    if (line.unitPrice < 0) {
      add('BR-27', `Position ${nummer} hat einen negativen Einzelpreis (${euro(line.unitPrice)}). Die EN 16931 lässt das nicht zu -- ein Abzug gehört als Abschlag abgebildet.`);
    }
    if (!effectiveTaxCode(line)) {
      add('BR-CO-17', `Position ${nummer} trägt den Steuersatz ${line.vatRate} %, für den es keinen Steuerschlüssel gibt.`);
      return;
    }
    // Differenzbesteuerung: ohne den Einkaufspreis laesst sich die Bemessungsgrundlage nicht
    // bilden -- und ohne sie waere die geschuldete Steuer geraten. § 25a Abs. 6 verlangt die
    // Aufzeichnung ohnehin je Gegenstand.
    const zeilenCode = effectiveTaxCode(line);
    if (zeilenCode && taxCodeDefinition(zeilenCode).marginScheme) {
      if (line.marginPurchasePrice === null || line.marginPurchasePrice === undefined) {
        add('§ 25a Abs. 6', `Position ${nummer} ist differenzbesteuert, trägt aber keinen Einkaufspreis. Ohne ihn lässt sich die Bemessungsgrundlage nicht bilden.`);
      } else if (line.vatRate !== 0) {
        add('§ 14a Abs. 6', `Position ${nummer} ist differenzbesteuert und darf keine Umsatzsteuer ausweisen -- im Beleg stehen ${line.vatRate} %.`);
      } else if (marginBase(line) === 0 && (line.marginPurchasePrice ?? 0) > line.netAmount) {
        // Verkauf unter Einkauf ist erlaubt (Marge = 0), aber fast immer ein Tippfehler.
        add('§ 25a Abs. 3', `Position ${nummer} wird unter dem Einkaufspreis verkauft -- die Marge ist damit null. Bitte prüfen.`);
      }
    }
    // Die Zentralregel je Zeile: Nettobetrag = Menge x Einzelpreis abzüglich der Abschläge.
    // Genau hier fiel bis 09/2026 der Zeilenrabatt auf, der zwar den Nettobetrag senkte, in der
    // XML aber nirgends stand -- die Zeile rechnete sich damit nicht auf.
    const erwartet = calculateInvoiceLineNet(line);
    if (!nearlyEqual(erwartet, line.netAmount)) {
      add('BR-CO-10', `Position ${nummer} rechnet sich nicht auf: ${euro(line.quantity * line.unitPrice)} abzüglich ${line.discountPercent ?? 0} % ergibt ${euro(erwartet)}, im Beleg steht ${euro(line.netAmount)}.`);
    }
  });

  // --- Steueraufteilung ------------------------------------------------------------------------
  const subtotals = taxSubtotals(invoice);
  if (!subtotals) {
    add('BR-CO-17', 'Die Rechnung enthält einen Steuersatz, der sich nicht abbilden lässt.');
  } else {
    for (const entry of subtotals) {
      const definition = taxCodeDefinition(entry.taxCode);
      const erwartet = round2(entry.taxableAmount * definition.rate / 100);
      // Toleranz 0,02: Zeilen werden einzeln gerundet, die Summe einmal.
      if (!nearlyEqual(erwartet, entry.taxAmount, 0.02)) {
        add('BR-CO-17', `Die Steuer zu ${definition.label} passt nicht zur Bemessungsgrundlage: ${euro(entry.taxableAmount)} ergäben ${euro(erwartet)}, ausgewiesen sind ${euro(entry.taxAmount)}.`);
      }
      // Jede Kategorie ausser S und AA braucht einen Befreiungsgrund (BT-120).
      if (definition.xrechnungCategory !== 'S' && !definition.xrechnungExemptionReason.trim()) {
        add('BR-E-10', `Zu „${definition.label}" fehlt der Befreiungsgrund, den die E-Rechnung verlangt.`);
      }
      // Reverse Charge und innergemeinschaftliche Lieferung gehen nur mit USt-IdNr. auf BEIDEN Seiten.
      if (entry.taxCode === 'RC13B' || entry.taxCode === 'IG') {
        if (!seller.vatId.trim()) {
          add(entry.taxCode === 'IG' ? 'BR-IC-05' : 'BR-AE-03', `„${definition.label}" verlangt die eigene USt-IdNr. -- sie fehlt in der Grundkonfiguration.`);
        }
        if (!buyer.vatId.trim()) {
          add(entry.taxCode === 'IG' ? 'BR-IC-06' : 'BR-AE-04', `„${definition.label}" verlangt die USt-IdNr. von ${buyer.name || 'dem Kunden'} -- sie fehlt im Kundenstamm.`);
        }
      }
      // Eine innergemeinschaftliche Lieferung muss sagen, WANN geliefert wurde.
      if (entry.taxCode === 'IG' && !invoice.serviceDate && !invoice.serviceEndDate) {
        add('BR-IC-11', 'Eine innergemeinschaftliche Lieferung braucht ein Leistungsdatum oder einen Leistungszeitraum.');
      }
      // § 25a Abs. 7 Nr. 3 UStG: Die Differenzbesteuerung schliesst die steuerfreie
      // innergemeinschaftliche Lieferung aus -- beides zusammen geht nicht.
      if (definition.marginScheme && subtotals.some((andere) => andere.taxCode === 'IG')) {
        add('§ 25a Abs. 7', 'Ein Beleg kann nicht zugleich differenzbesteuert und innergemeinschaftlich steuerfrei sein (§ 25a Abs. 7 Nr. 3 UStG). Bitte auf zwei Rechnungen trennen.');
      }
    }
  }

  // --- Belegsummen -----------------------------------------------------------------------------
  const zeilensumme = round2(invoice.lines.reduce((sum, line) => sum + line.netAmount, 0));
  if (!nearlyEqual(zeilensumme, round2(invoice.netTotal))) {
    add('BR-CO-10', `Die Summe der Positionen (${euro(zeilensumme)}) weicht vom Nettobetrag des Belegs (${euro(invoice.netTotal)}) ab.`);
  }
  const steuersumme = subtotals ? round2(subtotals.reduce((sum, entry) => sum + entry.taxAmount, 0)) : round2(invoice.vatTotal);
  if (!nearlyEqual(round2(invoice.netTotal + steuersumme), round2(invoice.grossTotal), 0.02)) {
    add('BR-CO-15', `Der Bruttobetrag (${euro(invoice.grossTotal)}) ist nicht die Summe aus Netto (${euro(invoice.netTotal)}) und Steuer (${euro(steuersumme)}).`);
  }

  // --- Referenz und Leistungszeitraum ----------------------------------------------------------
  if (!buyer.buyerReference.trim()) {
    add('BR-DE-15', 'Die Leitweg-ID bzw. Bestellreferenz fehlt -- in der XRechnung Pflicht.');
  }
  if (invoice.serviceEndDate && invoice.serviceDate && invoice.serviceEndDate < invoice.serviceDate) {
    add('BR-CO-19', 'Der Leistungszeitraum endet vor seinem Beginn.');
  }

  return findings;
}

/** Die Funde als Zeilen, wie sie der Anwender im Fenster sieht. */
export function en16931IssueMessages(findings: readonly En16931Finding[]): readonly string[] {
  return findings.map((finding) => `${finding.message} (${finding.rule})`);
}
